import { Inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/shared/services/analytics.service'; // Import the service
import { BootstrapRequestContext } from 'tbs-typings';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly EXCLUDE_PATTERNS = ['webvitals', 'pageviews'];
  private readonly LOG_THRESHOLD_MS = 20000;
  private bootstrapTokenSeeded = false;

  constructor(
    @Inject('BootstrapRequestContext') private readonly bootstrapRequestContext: BootstrapRequestContext,
    private readonly analyticsService: AnalyticsService) { } // Inject here

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const subscriptionKey = this.bootstrapRequestContext?.ocpApimSubscriptionKey;
    // adding okta token to session storage if not already present or set on page refresh
    if (!this.bootstrapTokenSeeded) {
      this.bootstrapTokenSeeded = true;
      const parsedOktaToken = this.bootstrapRequestContext?.oktaToken
        ? JSON.parse(this.bootstrapRequestContext.oktaToken)
        : null;
      if (parsedOktaToken) {
        sessionStorage.setItem('oktaToken', JSON.stringify(parsedOktaToken));
      }
    }
    const storedOktaToken = sessionStorage.getItem('oktaToken')
      ? JSON.parse(sessionStorage.getItem('oktaToken'))
      : null;
    const oktaTokenType = typeof storedOktaToken?.token_type === 'string'
      ? storedOktaToken.token_type.trim()
      : '';
    const oktaAccessToken = typeof storedOktaToken?.access_token === 'string'
      ? storedOktaToken.access_token.trim()
      : '';
    
    const apiBaseUrl = this.bootstrapRequestContext?.apiBaseUrl || '';
    const url = req.url.toLowerCase();

    const alljson = url.includes('/all.json');
    const shouldAddSubscriptionKey = !!subscriptionKey;
    const shouldAddOktaToken = !!oktaTokenType && !!oktaAccessToken;

    const requestWithApiHeaders = ((shouldAddSubscriptionKey || shouldAddOktaToken) && !alljson)
      ? req.clone({
          setHeaders: {
            ...(shouldAddSubscriptionKey && { 'Ocp-Apim-Subscription-Key': subscriptionKey }),
            ...(shouldAddOktaToken && { 'okta_Token': `${oktaTokenType} ${oktaAccessToken}` })
          }
        })
      : req;

    const shouldIntercept =
      (this.bootstrapRequestContext.logPageView && (requestWithApiHeaders.method === 'GET' || requestWithApiHeaders.method === 'POST')) &&
      url.startsWith(apiBaseUrl.toLowerCase()) &&
      !this.EXCLUDE_PATTERNS.some(pattern => url.includes(pattern));

    const relativeUrl = requestWithApiHeaders.url.substring(apiBaseUrl.length);
    if (!shouldIntercept) {
      return next.handle(requestWithApiHeaders);
    }

    const startTime = Date.now();
    const navigationId = this.analyticsService.getActiveNavigationId();
    return next.handle(requestWithApiHeaders).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          const duration = Date.now() - startTime;
          if (duration > this.LOG_THRESHOLD_MS) {
            const cleanUrl = relativeUrl.split('?')[0];
            this.analyticsService.appendAPILongTiming(cleanUrl, duration, navigationId);
          }
        }
      })
    );
  }
}
